/**
 * TutorialsTab - Composant pour l'onglet Tutoriels de la page Learn
 * Affiche les 8 tutoriels de l'application avec possibilité de les relancer
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  GraduationCap,
  Play,
  Star,
  Target,
  Zap,
  PiggyBank,
  BarChart3,
  Calculator,
  Calendar,
  BookOpen
} from 'lucide-react';
import OnboardingTutorial from '../OnboardingTutorial';
import {
  WATCHLIST_SLIDES,
  ANALYSIS_SLIDES,
  SCAN_AUTO_SLIDES,
  DCA_SLIDES,
  HISTORY_SLIDES,
  CALCULATOR_SLIDES,
  NEWS_ECO_SLIDES,
  LEARN_SLIDES
} from '../../config/tutorialSlides';

// Tutorial definitions
const TUTORIALS_LIST = [
  {
    id: 'watchlist',
    title: 'Mes Marchés (Watchlist)',
    description: 'Apprenez à gérer votre liste d\'actifs favoris',
    icon: Star,
    slides: WATCHLIST_SLIDES,
    storageKey: 'tutorial_watchlist'
  },
  {
    id: 'analysis',
    title: 'Analyse IA',
    description: 'Découvrez comment utiliser l\'analyse IA des graphiques',
    icon: Target,
    slides: ANALYSIS_SLIDES,
    storageKey: 'tutorial_analysis'
  },
  {
    id: 'scan-auto',
    title: 'Scan Auto',
    description: 'Configurez le scan automatique quotidien',
    icon: Zap,
    slides: SCAN_AUTO_SLIDES,
    storageKey: 'tutorial_scan-auto'
  },
  {
    id: 'dca',
    title: 'DCA Intelligent',
    description: 'Maîtrisez la stratégie Dollar Cost Averaging',
    icon: PiggyBank,
    slides: DCA_SLIDES,
    storageKey: 'tutorial_dca'
  },
  {
    id: 'history',
    title: 'Journal de Trading',
    description: 'Suivez vos performances et analysez vos trades',
    icon: BarChart3,
    slides: HISTORY_SLIDES,
    storageKey: 'tutorial_history'
  },
  {
    id: 'calculator',
    title: 'Calculateur',
    description: 'Calculez la taille de position optimale',
    icon: Calculator,
    slides: CALCULATOR_SLIDES,
    storageKey: 'tutorial_calculator'
  },
  {
    id: 'news-eco',
    title: 'News Économiques',
    description: 'Suivez le calendrier économique',
    icon: Calendar,
    slides: NEWS_ECO_SLIDES,
    storageKey: 'tutorial_news-eco'
  },
  {
    id: 'learn',
    title: 'Guide Formation',
    description: 'Introduction aux concepts ICT',
    icon: BookOpen,
    slides: LEARN_SLIDES,
    storageKey: 'tutorial_learn'
  }
];

// Section Card Component
const SectionCard = ({ icon: Icon, title, children, color = 'text-primary', bgColor = 'bg-primary/10' }) => (
  <Card className="bg-card/50 border-border/50 mb-4">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-2 text-lg">
        <div className={`p-2 rounded-lg ${bgColor}`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

// Tutorial Card Component
const TutorialCard = ({ tutorial, onLaunch }) => {
  const IconComponent = tutorial.icon;
  
  return (
    <div 
      className="p-4 bg-muted/30 rounded-lg border border-border/50 hover:border-emerald-500/50 transition-all"
      data-testid={`tutorial-card-${tutorial.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30">
          <IconComponent className="w-5 h-5 text-emerald-400" />
        </div>
        <div className="flex-1">
          <h4 className="font-semibold text-sm mb-1">{tutorial.title}</h4>
          <p className="text-xs text-muted-foreground mb-3">{tutorial.description}</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onLaunch(tutorial)}
            className="w-full border-emerald-500/50 hover:bg-emerald-500/10 hover:border-emerald-500"
            data-testid={`launch-tutorial-${tutorial.id}`}
          >
            <Play className="w-3 h-3 mr-2" />
            Lancer le tutoriel
          </Button>
        </div>
      </div>
    </div>
  );
};

const TutorialsTab = () => {
  const [activeTutorial, setActiveTutorial] = useState(null);

  return (
    <>
      {/* On-demand Tutorial Modal */}
      {activeTutorial && (
        <OnboardingTutorial
          pageId={activeTutorial.id}
          slides={activeTutorial.slides}
          storageKey={activeTutorial.storageKey}
          forceShow={true}
          onComplete={() => setActiveTutorial(null)}
        />
      )}

      <SectionCard 
        icon={GraduationCap} 
        title="Tutoriels de l'Application" 
        color="text-emerald-400" 
        bgColor="bg-emerald-500/10"
      >
        <p className="text-muted-foreground mb-6">
          Revoyez les tutoriels de chaque fonctionnalité de TRADEX AI. Cliquez sur &quot;Lancer&quot; pour revoir un tutoriel.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {TUTORIALS_LIST.map((tutorial) => (
            <TutorialCard 
              key={tutorial.id}
              tutorial={tutorial}
              onLaunch={setActiveTutorial}
            />
          ))}
        </div>
      </SectionCard>
    </>
  );
};

export default TutorialsTab;
